<template>
  <div id="geo" class="l-height-100 l-fixed l-top l-right geo" v-bind:class="[{'is-loading': isLoading}]">
    <div class="geo-map-controls-size">
      <span class="geo-map-grow" v-on:click="adjustMapSize('grow')">
        <i class="material-icons">arrow_drop_down</i>
      </span>
      <span class="geo-map-shrink" v-on:click="adjustMapSize()">
        <i class="material-icons ">arrow_drop_down</i>
      </span>
    </div>
    <div id="map" class="l-height-100 map"></div>
    <LoaderDots></LoaderDots>
  </div>
</template>

<script>
import LoaderDots from '@/components/LoaderDots'
import _ from 'lodash'
import L from 'leaflet'
// require('../../node_modules/leaflet.heat/dist/leaflet-heat.js')
require('../../node_modules/leaflet.markercluster/dist/leaflet.markercluster.js')
require('../../node_modules/leaflet-draw/dist/leaflet.draw.js')

export default {
  name: 'geo',

  components: {
    'LoaderDots': LoaderDots
  },

  props: {
    'mapData': {
      type: Array,
      required: false
    },
    'multipolygons': {
      type: Array,
      required: false
    },
    'markerClickAction': {
      type: String,
      required: false
    },
    'mapActive': {
      type: Number,
      required: false
    },
    'polygons': {
      type: Array,
      required: false
    }
  },

  data: function () {
    return {
      currentMarker: null,
      theMap: {},
      drawnItems: false,
      drawControls: {},
      mapDataLayerGroup: new L.LayerGroup(),
      multipolygonFeatureGroup: new L.FeatureGroup(),
      polygonFeatureGroup: new L.FeatureGroup(),
      mapSizes: ['s-map-collapsed', 's-map-default', 's-map-medium', 's-map-full'],
      currentMapSize: 's-map-default'
    }
  },

  mounted: function () {
    this.setMap()
    let app = document.getElementById('app')
    app.classList.add('s-map-default')
  },

  computed: {
    isLoading () {
      if (typeof this.mapData !== 'undefined') {
        return (this.mapData.length === 0) ? 1 : 0
      }

      if (typeof this.multipolygons !== 'undefined') {
        return (this.multipolygons.length === 0) ? 1 : 0
      }

      if (typeof this.polygons !== 'undefined') {
        return (this.polygons.length === 0) ? 1 : 0
      }
    },

    hasResults () {
      if (typeof this.mapData !== 'undefined') {
        return (this.mapData[0] === -1) ? 0 : 1
      }

      if (typeof this.multipolygons !== 'undefined') {
        return (this.multipolygons.length === 0) ? 0 : 1
      }

      if (typeof this.polygons !== 'undefined') {
        return (this.polygons.length === 0) ? 0 : 1
      }
    }
  },

  watch: {
    mapActive: function () {
      if (this.mapActive === 1) {
        this.activateDrawing()
      } else {
        this.deactivateDrawing()
      }
    },

    mapData: function () {
      if (this.hasResults) {
        this.refreshMapDataLayerGroup()
      }
    },

    multipolygons: function () {
      if (this.hasResults) {
        this.refreshMultipolygonFeatureGroup()
      }
    },

    polygons: function () {
      if (this.hasResults) {
        this.refreshPolygonFeatureGroup()
      }
    }
  },

  methods: {
    activateDrawing () {
      if (this.drawnItems) {
        // == If there are already items drawn then add them to the map.
        this.theMap.addLayer(this.drawnItems)
      } else {
        // == Otherwise create a blank feature group.
        this.drawnItems = L.featureGroup().addTo(this.theMap)
      }

      // == Make the controls for drawing.
      this.drawControls = new L.Control.Draw({
        draw: {
          polyline: false,
          rectangle: false,
          marker: false,
          circlemarker: false,
          circle: {
            allowIntersection: false,
            shapeOptions: {
              color: '#EF780E',
              opacity: 0.75
            },
            showArea: true
          },
          polygon: {
            allowIntersection: false,
            shapeOptions: {
              color: '#EF780E',
              opacity: 0.75
            },
            showArea: true
          }
        },
        edit: { featureGroup: this.drawnItems },
        position: 'topleft'
      })
      // == Add the drawing controls to the map.
      this.theMap.addControl(this.drawControls)
      // == Add drawing to drawnItems when something is drawn.
      this.theMap.on(L.Draw.Event.CREATED, (event) => {
        this.drawnItems.addLayer(event.layer)
      })
    },

    deactivateDrawing () {
      this.theMap.removeControl(this.drawControls)
      this.theMap.removeLayer(this.drawnItems)
    },

    adjustMapSize (direction) {
      let app = document.getElementById('app')
      this.mapSizes.forEach(function (mapSize) {
        if (app.classList.contains(mapSize)) {
          app.classList.remove(mapSize)
        }
      })

      let currentMapSize = this.currentMapSize
      let mapSizeIndex = this.mapSizes.findIndex(function (mapSize) {
        return currentMapSize === mapSize
      })

      if (direction === 'grow') {
        mapSizeIndex += 1
      } else {
        mapSizeIndex -= 1
      }

      this.currentMapSize = this.mapSizes[mapSizeIndex]
      app.classList.add(this.currentMapSize)

      this.theMap.invalidateSize()
    },

    refreshMapDataLayerGroup () {
      this.theMap.removeLayer(this.mapDataLayerGroup)
      this.setMapDataLayerGroup()
      this.theMap.addLayer(this.mapDataLayerGroup)
      this.setView()
    },

    refreshMultipolygonFeatureGroup () {
      this.theMap.removeLayer(this.multipolygonFeatureGroup)
      this.setMultipolygonFeatureGroup(this.multipolygons)
      this.theMap.addLayer(this.multipolygonFeatureGroup)
      this.theMap.fitBounds(this.multipolygonFeatureGroup.getBounds())
    },

    refreshPolygonFeatureGroup () {
      this.theMap.removeLayer(this.polygonFeatureGroup)
      this.setPolygonFeatureGroup()
      this.theMap.addLayer(this.polygonFeatureGroup)
      this.theMap.fitBounds(this.polygonFeatureGroup.getBounds())
    },

    setMap () {
      if (this.theMap) {
        let vm = this

        // == Bind leaflet map to HTML #map element on screen
        this.theMap = L.map(document.getElementById('map'), {
          zoomControl: false,
          scrollWheelZoom: false,
          minZoom: 11,
          maxBounds: L.latLngBounds(L.latLng(50, -120), L.latLng(46, -125)) // L.latLngBounds(L.latLng(47.77, -122.22), L.latLng(47.42, -122.46))
        }).setView([47.6062, -122.3321], 12).on('moveend', _.debounce(function () {
          let mapBounds = {
            ne: {lat: vm.theMap.getBounds()._northEast.lat, lng: vm.theMap.getBounds()._northEast.lng},
            sw: {lat: vm.theMap.getBounds()._southWest.lat, lng: vm.theMap.getBounds()._southWest.lng}
          }
          vm.$store.dispatch('filterMapBounds/set', mapBounds)
        }, 500))

        // == Add back zoom control in the upper right corner.
        let zoomControl = L.control.zoom({position: 'topleft'})
        this.theMap.addControl(zoomControl)

        // == Set desired map tiles
        L.tileLayer('http://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, Tiles courtesy of <a href="http://hot.openstreetmap.org/" target="_blank">Humanitarian OpenStreetMap Team</a>'
        }).addTo(this.theMap)

        // == Set marker image directory.
        L.Icon.Default.prototype.options.imagePath = '/static/'
      }
    },

    setMapDataLayerGroup () {
      let markers = []
      // let heat = []
      this.mapData.forEach(row => {
        let vm = this
        // == prepare markers
        markers.push(
          L.marker([row.lat, row.lng], {
            title: row.id
          }).on('click', function (e) {
            if (vm.currentMarker !== null) {
              vm.currentMarker.setIcon(L.icon({
                iconUrl: '/static/marker-icon.png',
                iconRetinaUrl: '/static/marker-icon-2x.png'
              }))
            }

            vm.currentMarker = this.setIcon(L.icon({
              iconUrl: '/static/marker-icon-yellow.png',
              iconRetinaUrl: '/static/marker-icon-2x-yellow.png'
            }))

            vm.theMap.panTo(this.getLatLng())
            vm.$store.dispatch(vm.markerClickAction, this.options.title)
          })
        )

        // == Prepare heat
        // heat.push([row.lat, row.lng, 0.4])
      })

      // == Add markers to layerGroup
      this.mapDataLayerGroup = new L.LayerGroup().addLayer(L.markerClusterGroup().addLayers(markers))

      // == Add heat to layerGroup
      // L.heatLayer(heat, {
      //   radius: 30,
      //   blur: 20,
      //   gradient: {0.3: 'blue', 0.65: '#FFFF00', 1: '#FC4D00'}
      // }).addTo(this.layerGroup)
    },

    setMultipolygonFeatureGroup () {
      this.setShapeFeatureGroup(this.multipolygons).forEach(layer => {
        this.multipolygonFeatureGroup.addLayer(layer)
      })
    },

    setPolygonFeatureGroup () {
      this.setShapeFeatureGroup(this.polygons).forEach(layer => {
        this.polygonFeatureGroup.addLayer(layer)
      })
    },

    setShapeFeatureGroup (shapes) {
      let layers = []

      shapes.forEach(shape => {
        let layer = L.polygon(shape.data, shape.options)

        if (shape.hasOwnProperty('click')) {
          layer.on('click', shape.click)
        }

        if (shape.hasOwnProperty('mouseover')) {
          layer.on('mouseover', shape.mouseover)
        }

        if (shape.hasOwnProperty('mouseout')) {
          layer.on('mouseout', shape.mouseout)
        }

        if (shape.hasOwnProperty('tooltip')) {
          layer.bindTooltip(shape.tooltip)
        }

        layers.push(layer)
      })

      return layers
    },

    setView () {
      // == Set view to the Seattle area.
      this.theMap.setView([47.6062, -122.3321], 11)
    }
  }
}
</script>

<style lang="scss">
@import "./src/sass/components/geo.scss";
</style>



// WEBPACK FOOTER //
// Geo.vue?27d53d9f